import mediator from '../utilities/mediator';
import log from '../utilities/log';

import { Study, StudyFull } from '../models';
import {
  mutationOptions,
  queryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { EntityState } from 'adc-sirius-module-common';

type StudiesQueryParams = {
  organizationKey?: string;
  state?: EntityState;
};

type StudyQueryParams = {
  key?: string;
};

type UpdateStudyVariables = {
  studyKey: Study['key'];
  updatedStudyValues: Partial<Study>;
};

export function studiesQueryOptions(
  params?: StudiesQueryParams,
  options?: Partial<UseQueryOptions<Study[]>>,
) {
  return queryOptions<Study[]>({
    ...options,
    queryKey: ['studies', params],
    queryFn: async () => {
      try {
        return await mediator.request<Study[]>('study:getStudies', {
          ...params,
        });
      } catch (error) {
        log.error(error);
        return [];
      }
    },
    enabled: !!params?.organizationKey,
  });
}

export function studyQueryOptions(
  params: StudyQueryParams,
  options?: Partial<UseQueryOptions<Study>>,
) {
  return queryOptions<Study>({
    ...options,
    queryKey: ['study', params],
    queryFn: async () => {
      try {
        return await mediator.request<Study>('study:getStudyByKey', {
          ...params,
        });
      } catch (error) {
        log.error(error);
        return {} as Study;
      }
    },
    enabled: !!params?.key,
  });
}

export function updateStudyMutateOptions(
  options?: Partial<UseMutationOptions<Study, Error, UpdateStudyVariables>>,
) {
  return mutationOptions<Study, Error, UpdateStudyVariables>({
    ...options,
    mutationFn: async ({ updatedStudyValues }: UpdateStudyVariables) => {
      try {
        return await mediator.request<Study>('study:updateStudy', {
          data: updatedStudyValues,
        });
      } catch (error) {
        log.error(error);
        throw error;
      }
    },
  });
}

export function studyDownloadMutateOptions(
  options?: Partial<
    UseMutationOptions<
      Blob,
      Error,
      {
        studyKey: string;
        subjectKeys?: string[];
        includeAudit?: boolean;
      }
    >
  >,
) {
  return mutationOptions<
    Blob,
    Error,
    {
      studyKey: string;
      subjectKeys?: string[];
      includeAudit?: boolean;
    }
  >({
    ...options,
    mutationFn: async ({ studyKey, subjectKeys, includeAudit }) => {
      try {
        return await mediator.request<Blob>('study:downloadData', {
          studyKey,
          subjectKeys,
          includeAudit,
        });
      } catch (error) {
        log.error(error);
        throw error;
      }
    },
  });
}

export function studyFullQueryOptions(
  params: StudyQueryParams,
  options?: Partial<UseQueryOptions<StudyFull>>,
) {
  return queryOptions<StudyFull>({
    ...options,
    queryKey: ['studyFull', params],
    queryFn: async () => {
      try {
        return await mediator.request<StudyFull>('study:getStudyFullByKey', {
          ...params,
        });
      } catch (error) {
        log.error(error);
        throw error;
      }
    },
    enabled: !!params?.key,
  });
}
